import React, { useEffect } from 'react';
import {
  PlusOutlined,
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  EditOutlined,
  FolderOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { Button, Card, List, Avatar, Image, Empty, Typography, Space, Tag, theme } from 'antd';
import { useStorage } from 'providers/StorageProvider';
import dayjs from 'libs/dayjs';
import useAuth from 'hooks/useAuth';
import useFiles from 'hooks/useFiles';
import useTenantFlags from 'hooks/useTenantFlags';

const { Text, Title } = Typography;

const Carpeta0 = ({ id }) => {
  const { token } = theme.useToken();
  const {
    getCarpeta0,
    carpeta0,
    loadingGetCarpeta0,
    createCarpeta0,
    loadingCreateCarpeta0,
    openCreate,
    openEdit,
    openDelete,
  } = useStorage();
  const { user } = useAuth();
  const { readOnly } = useTenantFlags();
  const { isImage, getDownloadUrl, handlePreview, getImagePreviewProps, getAvatarProps } =
    useFiles();

  useEffect(() => {
    getCarpeta0(id);
  }, [getCarpeta0, id]);

  // Función para obtener la extensión del archivo
  const getFileExtension = (filename) => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop().toUpperCase() : '';
  };

  // Función para obtener el tamaño del archivo formateado
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Card
      title={
        <Space>
          <FolderOutlined style={{ color: token.colorPrimary }} />
          <Title level={4} style={{ margin: 0 }}>
            Carpeta Cero
          </Title>
          {carpeta0 && (
            <Tag color="blue">
              {carpeta0.Files?.length || 0} archivo
              {carpeta0.Files?.length !== 1 ? 's' : ''}
            </Tag>
          )}
        </Space>
      }
      loading={loadingGetCarpeta0}
      extra={
        !carpeta0 ? (
          <Button
            type="primary"
            loading={loadingCreateCarpeta0}
            onClick={() => createCarpeta0(id)}
            icon={<FolderOutlined />}
            size="large"
          >
            Crear Carpeta
          </Button>
        ) : (
          <Button
            type="primary"
            onClick={() => openCreate(carpeta0)}
            icon={<PlusOutlined />}
            size="large"
          >
            Añadir Archivo
          </Button>
        )
      }
      style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
    >
      {carpeta0 && carpeta0.Files.length > 0 ? (
        <List
          itemLayout="horizontal"
          dataSource={carpeta0.Files}
          renderItem={(file) => (
            <List.Item
              style={{
                padding: '16px',
                margin: '8px 0',
                borderRadius: '8px',
                border: '1px solid #f0f0f0',
                backgroundColor: '#fafafa',
                transition: 'all 0.3s ease',
              }}
              actions={[
                isImage(file.file_type) && (
                  <Button
                    icon={<EyeOutlined />}
                    type="text"
                    onClick={() => handlePreview(file)}
                    title="Vista previa"
                    style={{ color: token.colorPrimary }}
                  />
                ),
                <a
                  key="download"
                  href={getDownloadUrl(file.id)}
                  download={file.filename}
                  title="Descargar"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    icon={<DownloadOutlined />}
                    type="text"
                    style={{ color: token.colorSuccess }}
                    title="Descargar"
                  />
                </a>,
                !readOnly && (
                  <Button
                    key="edit"
                    type="text"
                    onClick={() => openEdit(file, carpeta0)}
                    disabled={user.is_administrador}
                    icon={<EditOutlined />}
                    title="Editar"
                    style={{ color: '#faad14' }}
                  />
                ),
                !readOnly && (
                  <Button
                    icon={<DeleteOutlined />}
                    key="delete"
                    type="text"
                    disabled={user.is_administrador}
                    onClick={() => openDelete(file.id, carpeta0)}
                    title="Eliminar"
                    style={{ color: '#ff4d4f' }}
                  />
                ),
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    {...getAvatarProps(file)}
                    style={{
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  />
                }
                title={
                  <Space>
                    <Text strong style={{ fontSize: '16px' }}>
                      {file.title}
                    </Text>
                    <Tag color="blue" style={{ fontSize: '11px' }}>
                      {getFileExtension(file.filename)}
                    </Tag>
                  </Space>
                }
                description={
                  <Space direction="vertical" size={4}>
                    <Text type="secondary" style={{ fontSize: '14px' }}>
                      {file.description}
                    </Text>
                    <Space>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        <FileTextOutlined /> {dayjs(file.createdAt).format('DD/MM/YYYY HH:mm')}
                      </Text>
                      {file.file_size && (
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          • {formatFileSize(file.file_size)}
                        </Text>
                      )}
                    </Space>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      ) : carpeta0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Space direction="vertical" size={16}>
              <Text type="secondary">No hay archivos en esta carpeta</Text>
              <Button type="primary" onClick={() => openCreate(carpeta0)} icon={<PlusOutlined />}>
                Añadir primer archivo
              </Button>
            </Space>
          }
        />
      ) : null}
      <Image {...getImagePreviewProps()} />
    </Card>
  );
};

export default Carpeta0;
